import React from 'react';

import { Chip } from '../../ui';
import { Produto } from '../../api/sga-produto/sga-produtoApi';
import { numeroParaMoeda } from '../../utils/moeda';

/**
 * A lista de produtos com seleção — **um componente para as duas telas**
 * (at-62, frente D/D1).
 *
 * ## Por que existe
 *
 * `/produto` (etapa 4 da adesão, reescrita na at-60) e `/cotacao-produtos`
 * mostram a **mesma coisa**: os produtos do perfil do veículo, com os
 * compulsórios pré-marcados e travados, o preço de cada um e o marcador de
 * obrigatório. Eram duas implementações — uma no design system, outra ainda em
 * `<li class="produto-item">` com `<span>` laranja escrito à mão. Convenção da
 * at-24: regra repetida em duas telas vira componente.
 *
 * ## Camada
 *
 * Mora em `components/` e não em `src/ui` porque conhece o domínio: o shape
 * `Produto` de `api/sga-produto` e a ideia de "produto compulsório". O kit que
 * ele usa (`Chip`) é puro — precedente do `FormularioAssociado` (`13-ui-kit.md`).
 *
 * ## ⚠️ O que ele NÃO decide
 *
 * Nem quais produtos vêm, nem a ordem (compulsórios → `padrao` → demais, at-29),
 * nem quais são compulsórios, nem o que vai no payload. Tudo isso é da tela —
 * o componente só desenha o que recebe, na ordem em que recebe.
 */

/**
 * Qual descrição exibir.
 *
 * 🔴 **As duas telas mostram campos DIFERENTES, e isso é de propósito:**
 * `/produto` exibe a descrição do **boleto** (é o que o associado verá na
 * cobrança) e a cotação exibe a **comercial** — que é também a que vai no
 * `produtos[]` do PDF, onde o backend casa os compulsórios por descrição
 * normalizada. Unificar mudaria o documento.
 */
export type CampoDescricaoProduto = 'descricao_produto' | 'descricao_produto_boleto';

interface PropsListaProdutos {
  produtos: Produto[];
  /** Códigos marcados. */
  selecionados: string[];
  /** Códigos que ficam marcados e **travados** (flag `compulsorio` do SGA). */
  compulsorios: string[];
  /** Trava a lista inteira enquanto uma gravação corre. */
  desabilitado?: boolean;
  campoDescricao?: CampoDescricaoProduto;
  aoAlternar: (codigo: string) => void;
}

const ListaProdutos: React.FC<PropsListaProdutos> = ({
  produtos,
  selecionados,
  compulsorios,
  desabilitado = false,
  campoDescricao = 'descricao_produto_boleto',
  aoAlternar,
}) => (
  <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
    {produtos.map((produto) => {
      const obrigatorio = compulsorios.includes(produto.codigo_produto);

      return (
        <li key={produto.codigo_produto}>
          <label
            className={[
              'tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-p-3',
              'tw-text-sm tw-text-texto-corpo tw-transition-colors',
              obrigatorio
                ? 'tw-border-superficie-borda tw-bg-superficie'
                : 'tw-cursor-pointer tw-border-superficie-borda tw-bg-white hover:tw-bg-superficie',
            ].join(' ')}
          >
            <input
              type="checkbox"
              checked={selecionados.includes(produto.codigo_produto)}
              disabled={obrigatorio || desabilitado}
              onChange={() => aoAlternar(produto.codigo_produto)}
              /* ⚠️ `accent-brand` pinta o marcador com a cor da marca. Sem ele o
                 checkbox herda o azul do sistema — ou o global
                 `input[type="checkbox"]` que veio de `produto.css` e hoje vive em
                 `ui/tokens/legado-compartilhado.css`. */
              className="tw-h-4 tw-w-4 tw-shrink-0 tw-cursor-pointer tw-rounded tw-accent-brand disabled:tw-cursor-not-allowed"
            />
            <span className="tw-min-w-0 tw-flex-1">{produto[campoDescricao]}</span>
            {/* ⚠️ Marcador com TEXTO, nunca só cor (spec 02 §9 / at-54). Antes da
                at-62 a cotação usava um `<span>` laranja escrito à mão. */}
            {obrigatorio && <Chip variante="neutro">obrigatório</Chip>}
            <span className="tw-shrink-0 tw-text-xs tw-font-semibold tw-text-texto-suave">
              {numeroParaMoeda(parseFloat(produto.valor_produto || '0'))}
            </span>
          </label>
        </li>
      );
    })}
  </ul>
);

export default ListaProdutos;
